// 🔧 vote_generator.js
// ✅ Purpose: Generates realistic demo votes and Detailed Scorecard evaluations for the LGV Seed Engine
// ✅ Features:
// - Preserves the established weighted vote generator used by non-scorecard Experiences
// - Generates Detailed Scorecard evaluations when real scoring metadata is available
// - Uses actual factor and highlight IDs produced by the Scoring Builder
// - Creates one unique synthetic evaluation per voter + entry relationship
// - Prevents duplicate voter/entry scorecard evaluations
// - Ensures all available entries receive scorecard evaluations before additional rounds are added
// - Produces deterministic score variation so demos remain believable and testable
// - Gives stronger-positioned entries somewhat higher averages without producing identical scores
// - Supports multiple scored factors and arbitrary positive criterion maximums
// - Keeps scoring generation independent from SAP or any other specific industry
// - Fully block-commented for clarity and future devs

// ============================================================
// STANDARD WEIGHTED DEMO VOTING
// ============================================================
// This is the pre-existing Demo Engine behavior.
//
// It remains unchanged for Experiences that do not provide scored
// Detailed Scorecard configuration.
// ============================================================

const buildWeightedEntryPool = (entries = []) => {
  const weightedEntries = [];

  entries.forEach((entry, index) => {
    const weight =
      Math.max(
        2,
        entries.length - index + 2
      );

    for (
      let i = 0;
      i < weight;
      i += 1
    ) {
      weightedEntries.push(entry);
    }
  });

  return weightedEntries;
};

export const generateDemoVotes = ({
  users = [],
  entries = [],
  voteCount = 100,
} = {}) => {
  const votes = [];

  if (
    !users.length ||
    !entries.length
  ) {
    return votes;
  }

  const weightedEntries =
    buildWeightedEntryPool(
      entries
    );

  for (
    let index = 0;
    index < voteCount;
    index += 1
  ) {
    const user =
      users[
        index % users.length
      ];

    const entry =
      weightedEntries[
        Math.floor(
          Math.random() *
          weightedEntries.length
        )
      ];

    votes.push({
      voterId:
        user.id,

      voterName:
        user.displayName,

      entryId:
        entry.id,

      entryTitle:
        entry.title,

      categoryId:
        entry.categoryId,

      categoryName:
        entry.categoryName,

      ballotPosition:
        1,
    });
  }

  votes.sort(
    () =>
      Math.random() - 0.5
  );

  return votes;
};

// ============================================================
// DETAILED SCORECARD HELPERS
// ============================================================

const clampNumber = (
  value,
  minimum,
  maximum
) =>
  Math.min(
    Math.max(
      Number(value || 0),
      minimum
    ),
    maximum
  );

/**
 * ------------------------------------------------------------
 * HAS SCORED HIGHLIGHTS
 * ------------------------------------------------------------
 * A factor becomes a Detailed Scorecard factor when at least one
 * highlight/criterion has a positive maxPoints value.
 */
export const hasScoredHighlights = (
  scoringFactors = []
) =>
  (scoringFactors || []).some(
    (factor) =>
      (factor?.highlights || []).some(
        (highlight) =>
          Number(
            highlight?.maxPoints || 0
          ) > 0
      )
  );

/**
 * ------------------------------------------------------------
 * BUILD UNIQUE EVALUATION PAIRS
 * ------------------------------------------------------------
 * Production Points voting stores one vote per user + entry.
 *
 * Therefore synthetic scorecard evaluations must also avoid
 * duplicate user/entry relationships.
 *
 * Pair generation proceeds in entry rounds so every entry gets
 * evaluated evenly instead of filling one partner before another.
 */
const buildUniqueEvaluationPairs = ({
  users = [],
  entries = [],
  voteCount = 0,
} = {}) => {
  if (
    !users.length ||
    !entries.length ||
    Number(voteCount || 0) <= 0
  ) {
    return [];
  }

  const maximumUniquePairs =
    users.length *
    entries.length;

  const requestedCount =
    Math.min(
      Math.max(
        Math.floor(
          Number(voteCount || 0)
        ),
        0
      ),
      maximumUniquePairs
    );

  const pairs = [];
  const seenPairs =
    new Set();

  let round = 0;

  while (
    pairs.length <
      requestedCount &&
    round < users.length
  ) {
    for (
      let entryIndex = 0;
      entryIndex < entries.length;
      entryIndex += 1
    ) {
      if (
        pairs.length >=
        requestedCount
      ) {
        break;
      }

      // ------------------------------------------------------
      // Rotate the starting voter for each entry.
      //
      // This prevents every partner from being evaluated by the
      // exact same first subset of synthetic voters.
      // ------------------------------------------------------

      const userIndex =
        (
          round +
          entryIndex * 7
        ) %
        users.length;

      const user =
        users[userIndex];

      const entry =
        entries[entryIndex];

      const pairKey =
        `${user.id}::${entry.id}`;

      if (
        seenPairs.has(
          pairKey
        )
      ) {
        continue;
      }

      seenPairs.add(
        pairKey
      );

      pairs.push({
        user,
        userIndex,
        entry,
        entryIndex,
        round,
      });
    }

    round += 1;
  }

  return pairs;
};

/**
 * ------------------------------------------------------------
 * BUILD REALISTIC CRITERION SCORE
 * ------------------------------------------------------------
 * This intentionally avoids pure Math.random().
 *
 * Stable score generation gives us:
 * - repeatable demos,
 * - repeatable E2E assertions,
 * - meaningful ranking differences,
 * - natural variation between judges and criteria.
 *
 * Earlier entries receive a modest performance advantage only for
 * demo presentation. The values are still varied per judge and
 * criterion so the dashboard does not look artificially uniform.
 */
const buildCriterionPoints = ({
  maxPoints,
  entryIndex,
  entryCount,
  userIndex,
  criterionIndex,
  factorIndex,
  round,
} = {}) => {
  const safeMax =
    Math.max(
      Number(maxPoints || 0),
      0
    );

  if (
    safeMax <= 0
  ) {
    return 0;
  }

  const safeEntryCount =
    Math.max(
      Number(entryCount || 1),
      1
    );

  const entryPositionRatio =
    safeEntryCount <= 1
      ? 0
      : Number(entryIndex || 0) /
        (safeEntryCount - 1);

  // ----------------------------------------------------------
  // Strongest demo entries trend near ~90% of available points.
  // Lower-positioned entries trend near ~68%.
  // ----------------------------------------------------------

  const performanceRatio =
    0.9 -
    entryPositionRatio * 0.22;

  // ----------------------------------------------------------
  // Give different criteria slight personality.
  // ----------------------------------------------------------

  const criterionAdjustment =
    (
      (
        Number(criterionIndex || 0) %
        5
      ) -
      2
    ) *
    0.018;

  // ----------------------------------------------------------
  // Deterministic judge variation between roughly -8% and +8%.
  // ----------------------------------------------------------

  const variationBucket =
    (
      Number(userIndex || 0) * 3 +
      Number(entryIndex || 0) * 5 +
      Number(criterionIndex || 0) * 7 +
      Number(factorIndex || 0) * 11 +
      Number(round || 0)
    ) %
    5;

  const judgeAdjustment =
    (
      variationBucket -
      2
    ) *
    0.04;

  const scoreRatio =
    clampNumber(
      performanceRatio +
        criterionAdjustment +
        judgeAdjustment,
      0.2,
      1
    );

  // ----------------------------------------------------------
  // Scored criteria should represent an actual rating.
  //
  // For SAP's /5 criteria this naturally produces integer scores
  // between 1 and 5.
  // ----------------------------------------------------------

  return clampNumber(
    Math.round(
      safeMax *
      scoreRatio
    ),
    1,
    safeMax
  );
};

/**
 * ------------------------------------------------------------
 * GENERATE DETAILED SCORECARD DEMO EVALUATIONS
 * ------------------------------------------------------------
 *
 * Generic contract:
 * - scoringFactors come from scoring_builder createdFactors
 * - all IDs therefore correspond to actual Firestore documents
 * - each vote contains canonical scores[] / checkedHighlights[]
 * - each user/entry pair appears at most once
 */
export const generateDetailedScorecardDemoVotes = ({
  users = [],
  entries = [],
  scoringFactors = [],
  voteCount = 100,
  maxBallotPoints = null,
} = {}) => {
  if (
    !users.length ||
    !entries.length ||
    !hasScoredHighlights(
      scoringFactors
    )
  ) {
    return [];
  }

  const activeFactors =
    (scoringFactors || []).filter(
      (factor) =>
        factor?.active !== false
    );

  const calculatedBallotMaximum =
    activeFactors.reduce(
      (
        total,
        factor
      ) =>
        total +
        Number(
          factor?.maxPoints || 0
        ),
      0
    );

  const resolvedBallotMaximum =
    Number(
      maxBallotPoints ||
      calculatedBallotMaximum ||
      100
    );

  const pairs =
    buildUniqueEvaluationPairs({
      users,
      entries,
      voteCount,
    });

  return pairs.map(
    (
      {
        user,
        userIndex,
        entry,
        entryIndex,
        round,
      },
      evaluationIndex
    ) => {
      const scores =
        activeFactors.map(
          (
            factor,
            factorIndex
          ) => {
            const activeHighlights =
              (
                factor?.highlights ||
                []
              ).filter(
                (highlight) =>
                  highlight?.active !==
                  false
              );

            const checkedHighlights =
              activeHighlights.map(
                (
                  highlight,
                  criterionIndex
                ) => {
                  const criterionMax =
                    Number(
                      highlight?.maxPoints ||
                      0
                    );

                  const pointsAwarded =
                    criterionMax > 0
                      ? buildCriterionPoints({
                          maxPoints:
                            criterionMax,

                          entryIndex,

                          entryCount:
                            entries.length,

                          userIndex,

                          criterionIndex,

                          factorIndex,

                          round,
                        })
                      : 0;

                  return {
                    highlightId:
                      highlight.id,

                    highlightName:
                      highlight.name,

                    checked:
                      criterionMax > 0,

                    pointsAwarded,

                    maxPoints:
                      criterionMax,
                  };
                }
              );

            const scoredHighlights =
              checkedHighlights.filter(
                (highlight) =>
                  Number(
                    highlight.maxPoints ||
                    0
                  ) > 0
              );

            const factorPoints =
              scoredHighlights.reduce(
                (
                  total,
                  highlight
                ) =>
                  total +
                  Number(
                    highlight.pointsAwarded ||
                    0
                  ),
                0
              );

            const factorMaximum =
              scoredHighlights.length > 0
                ? scoredHighlights.reduce(
                    (
                      total,
                      highlight
                    ) =>
                      total +
                      Number(
                        highlight.maxPoints ||
                        0
                      ),
                    0
                  )
                : Number(
                    factor?.maxPoints ||
                    0
                  );

            return {
              factorId:
                factor.id,

              factorName:
                factor.name,

              pointsAwarded:
                factorPoints,

              maxPoints:
                factorMaximum,

              checkedHighlights,
            };
          }
        );

      const totalPoints =
        scores.reduce(
          (
            total,
            score
          ) =>
            total +
            Number(
              score.pointsAwarded ||
              0
            ),
          0
        );

      return {
        voterId:
          user.id,

        voterName:
          user.displayName,

        entryId:
          entry.id,

        entryTitle:
          entry.title,

        categoryId:
          entry.categoryId,

        categoryName:
          entry.categoryName,

        ballotPosition:
          1,

        votingType:
          "points",

        totalPoints,

        maxBallotPoints:
          resolvedBallotMaximum,

        scores,

        evaluationIndex:
          evaluationIndex + 1,
      };
    }
  );
};